import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { queryKeys } from '@sportninja/common/config/queryKeys'
import type {
  GamePatch,
  PlannedPatch,
  PlannedSkip,
} from '@sportninja/common/features/game/gameListView/bulkPlan'
import {
  applyPatchToGame,
  withCompatibilityFields,
} from '@sportninja/common/features/game/gameListView/bulkPlan'
import { gameDayKey } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { MutationOutcome } from '@sportninja/common/features/game/gameListView/reconcile'
import {
  classifyReconciliation,
  isRejection,
  rehydrateVenueFacilities,
} from '@sportninja/common/features/game/gameListView/reconcile'
import { runWithConcurrency } from '@sportninja/common/features/game/gameListView/runWithConcurrency'
import type {
  CivilDateRange,
  GameLike,
  TeamRef,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'
import {
  getGame,
  updateGame,
} from '@sportninja/common/services/game/gameService'

/**
 * In-flight PUTs. Four keeps a ten-game retime under a couple of seconds
 * without handing the API a burst it will queue anyway — and every failure is
 * independent, so a slow lane never blocks the others.
 */
export const BULK_CONCURRENCY = 4

/** Reconciliation polls before a row is declared genuinely unconfirmed. */
export const RECONCILE_ATTEMPTS = 3

/** Gap between polls — long enough for a slow write to land, short enough to feel live. */
export const RECONCILE_DELAY_MS = 1500

export interface RowResult {
  gameId: string
  outcome: MutationOutcome
  /** Field-keyed messages from a 422, ready to render next to the cell. */
  invalidFields?: Record<string, string>
  message?: string
  /** Server values to show when somebody else moved the row underneath us. */
  conflictingGame?: GameLike
}

export interface BulkRunSummary {
  saved: number
  rejected: number
  conflicted: number
  unconfirmed: number
  /** Rolled back after reconciliation proved the write never landed. */
  notSaved: number
  skipped: PlannedSkip[]
  results: RowResult[]
}

interface GamesPage {
  data: GameLike[]
  meta?: unknown
}

interface InfiniteGames {
  pages: GamesPage[]
  pageParams: unknown[]
}

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

const readInvalidFields = (
  error: unknown
): Record<string, string> | undefined => {
  const bag = (error as { invalid_fields?: Record<string, unknown> })
    ?.invalid_fields
  if (bag == null || typeof bag !== 'object') {
    return undefined
  }
  return Object.entries(bag).reduce<Record<string, string>>(
    (acc, [key, value]) => ({
      ...acc,
      [key]: Array.isArray(value) ? String(value[0]) : String(value),
    }),
    {}
  )
}

export interface UseGameBulkEditArgs {
  queryKey: unknown[]
  venuesById: Map<string, VenueRef>
  /** Every selectable team, keyed by uid — the optimistic team swap reads it. */
  teamsById: Map<string, TeamRef>
  /** The window currently on screen — used to spot rows that move out of it. */
  civilRange: CivilDateRange | null
}

export interface UseGameBulkEditResult {
  isRunning: boolean
  /** Rows with a PUT in flight — non-editable until they settle. */
  pendingIds: Set<string>
  /**
   * Rows whose new date or venue no longer matches the active filter. Frozen
   * membership: without it a row vanishes the instant it is patched, which
   * reads as "my edit deleted the game".
   */
  retainedOutsideFilterIds: Set<string>
  /** True while a run summary is up — the grid must not reorder underneath it. */
  isSummaryOpen: boolean
  summary: BulkRunSummary | null
  runPatches: (
    games: GameLike[],
    patches: PlannedPatch[],
    skipped?: PlannedSkip[]
  ) => Promise<BulkRunSummary>
  dismissSummary: () => void
}

export const useGameBulkEdit = ({
  queryKey,
  venuesById,
  teamsById,
  civilRange,
}: UseGameBulkEditArgs): UseGameBulkEditResult => {
  'use memo'

  const queryClient = useQueryClient()
  const [isRunning, setIsRunning] = useState(false)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const [retainedOutsideFilterIds, setRetained] = useState<Set<string>>(
    new Set()
  )
  const [summary, setSummary] = useState<BulkRunSummary | null>(null)

  /**
   * Runs are concurrent, so their state has to be UNIONED rather than replaced.
   *
   * Single-cell edits on different rows overlap freely — an admin fixing four
   * times in a row will start the next before the last has answered. Storing
   * one run's pending set, summary and retention directly in state meant the
   * second run unlocked the first's row, whichever finished first cleared
   * everything, and the last to finish overwrote the summary. All three are
   * silent: the row looks editable again while its PUT is still in the air.
   *
   * Refs, not state: these are written from inside async workers where a stale
   * closure over `useState` would drop concurrent updates.
   */
  const runSeqRef = useRef(0)
  const pendingByRunRef = useRef(new Map<number, Set<string>>())
  const accumulatedRef = useRef<{
    results: RowResult[]
    skipped: PlannedSkip[]
    retained: Set<string>
  }>({ results: [], skipped: [], retained: new Set() })

  /**
   * One in-flight PUT per game, ever.
   *
   * The grid blocks editing a pending row, but nothing stops a bulk run and a
   * cell edit from targeting the same game. Two PUTs racing on one row can land
   * in either order, so the row would end up holding whichever the server saw
   * last rather than what the admin did last.
   */
  const laneRef = useRef(new Map<string, Promise<unknown>>())

  const publishPending = () => {
    const union = new Set<string>()
    pendingByRunRef.current.forEach((ids) => {
      ids.forEach((id) => union.add(id))
    })
    setPendingIds(union)
    setIsRunning(pendingByRunRef.current.size > 0)
  }

  const serializePerGame = <TResult>(
    gameId: string,
    work: () => Promise<TResult>
  ): Promise<TResult> => {
    const prior = laneRef.current.get(gameId) ?? Promise.resolve()
    const next = prior.then(work, work)
    //  Swallow on the LANE only; the caller still sees the real rejection.
    laneRef.current.set(
      gameId,
      next.then(
        () => undefined,
        () => undefined
      )
    )
    return next
  }

  /**
   * The row as the cache holds it right now.
   *
   * A serialized second edit to the same game must build on what the first one
   * wrote, not on the snapshot captured when it was queued.
   */
  const readRowFromCache = (gameId: string): GameLike | undefined => {
    const cached = queryClient.getQueryData<InfiniteGames>(queryKey)
    return cached?.pages
      .flatMap((page) => page.data ?? [])
      .find((row) => row.id === gameId)
  }

  /** Replaces one row inside the ACTIVE infinite query, in place. */
  const patchRowInCache = (gameId: string, next: GameLike) => {
    queryClient.setQueryData<InfiniteGames>(queryKey, (old) => {
      if (old == null) {
        return old
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: (page.data ?? []).map((row) =>
            row.id === gameId ? next : row
          ),
        })),
      }
    })
  }

  /**
   * Takes the DIVISION the server ended up with, and nothing else.
   *
   * Deliberately a field lift rather than a row replacement: `GameResource`
   * (what the PUT answers with) omits the venue facilities `GamesResource`
   * carries, so adopting the whole thing would leave the Surface editor with
   * nothing to offer. `schedule` is self-contained and is the one value the
   * client cannot derive for itself.
   *
   * A response with no `schedule` — an older API, or a shape that changed —
   * leaves the row exactly as the optimistic patch left it.
   */
  const adoptServerSchedule = (gameId: string, saved: GameLike) => {
    const schedule = saved?.schedule
    if (schedule?.id == null) {
      return
    }
    const current = readRowFromCache(gameId)
    if (current === undefined || current.schedule?.id === schedule.id) {
      return
    }
    patchRowInCache(gameId, { ...current, schedule })
  }

  /**
   * Every other `scheduleGames` variant — List, Calendar, Details, Team, and
   * each filter combination — holds its own copy of this row under its own key.
   * Patching only the active one leaves an inactive-but-still-FRESH cache that
   * serves pre-edit rows the moment the admin switches view.
   *
   * `refetchType: 'none'` marks them stale without firing a single request: the
   * point is to invalidate, not to yank the current view's data out from under
   * the user mid-edit.
   */
  const markOtherVariantsStale = (editedGameIds: string[]) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.scheduleGames.all,
      refetchType: 'none',
    })

    /*
     * The per-game caches too. `queryKeys.game.byId` backs the Game page and
     * the scoring screens; without this an admin who retimes a game here and
     * then opens it still sees the old start time, served fresh from cache.
     *
     * A separate root from `scheduleGames`, so one call cannot cover both.
     */
    editedGameIds.forEach((gameId) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.game.all, gameId],
        refetchType: 'none',
      })
    })
  }

  const rollbackRow = (gameId: string, preEdit: GameLike) => {
    patchRowInCache(gameId, preEdit)
  }

  /**
   * Establishes what really happened when the PUT's own answer is unusable.
   *
   * `GamesController::update` saves the game and then rebuilds rosters with no
   * transaction around the pair, so a 5xx or a dropped connection is genuinely
   * ambiguous — the row may well be written. Rolling back on that guess would
   * overwrite a real save; retrying could double-apply a shift.
   */
  const reconcile = async (
    gameId: string,
    preEdit: GameLike,
    intendedPatch: GamePatch
  ): Promise<RowResult> => {
    for (let attempt = 0; attempt < RECONCILE_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await sleep(RECONCILE_DELAY_MS)
      }
      let observed: GameLike | null = null
      try {
        observed = await getGame(gameId)
      } catch {
        continue
      }

      //  The single-game resource drops the venue's facilities, so a row
      //  rehydrated straight from it would lose its surface options.
      const hydrated = rehydrateVenueFacilities(observed, venuesById)
      const outcome = classifyReconciliation({
        intendedPatch,
        preEdit,
        observed: hydrated,
      })

      if (outcome === 'saved') {
        patchRowInCache(gameId, hydrated)
        return { gameId, outcome }
      }
      if (outcome === 'not-saved') {
        rollbackRow(gameId, preEdit)
        return { gameId, outcome }
      }
      if (outcome === 'conflict') {
        patchRowInCache(gameId, hydrated)
        return { gameId, outcome, conflictingGame: hydrated }
      }
    }

    //  No rollback, no auto-retry — the row keeps its patched appearance and the
    //  failure panel says plainly that we could not confirm it.
    return { gameId, outcome: 'unconfirmed' }
  }

  const runPatches = async (
    games: GameLike[],
    patches: PlannedPatch[],
    skipped: PlannedSkip[] = []
  ): Promise<BulkRunSummary> => {
    const byId = new Map(games.map((game) => [game.id, game]))
    const targets = patches.filter((planned) => byId.has(planned.gameId))

    const runId = runSeqRef.current
    runSeqRef.current += 1
    pendingByRunRef.current.set(
      runId,
      new Set(targets.map((planned) => planned.gameId))
    )
    publishPending()

    const movedOutside = new Set<string>()

    const settled = await runWithConcurrency(
      targets,
      BULK_CONCURRENCY,
      async (planned): Promise<RowResult> =>
        serializePerGame(planned.gameId, async () => {
          //  Re-read at execution time: a serialized second edit to this row must
          //  build on what the first one wrote, not on the snapshot captured when
          //  this run was queued.
          const preEdit =
            readRowFromCache(planned.gameId) ??
            (byId.get(planned.gameId) as GameLike)
          const patch = withCompatibilityFields(preEdit, planned.patch)

          //  Patched BEFORE the request, not after. The whole point of the
          //  feature is that ten games visibly move at once instead of the admin
          //  waiting on ten round-trips — and it is what gives "unconfirmed" a
          //  row to leave alone rather than a decision to guess at.
          //
          //  The PUT response is NOT usable as a row replacement — it omits the
          //  venue facilities the list resource carries — so the row is rebuilt
          //  from the patch we know we sent.
          const optimistic = applyPatchToGame(preEdit, planned.patch, {
            venuesById,
            teamsById,
          })
          patchRowInCache(planned.gameId, optimistic)

          //  An absent bound is open-ended, so a one-sided range can only push
          //  a row out on the side it actually constrains.
          const nextDay = gameDayKey(optimistic)
          const movedOut =
            civilRange !== null &&
            ((civilRange.start !== null && nextDay < civilRange.start) ||
              (civilRange.endExclusive !== null &&
                nextDay >= civilRange.endExclusive))
          if (movedOut) {
            movedOutside.add(planned.gameId)
          }

          try {
            const saved = await updateGame(planned.gameId, patch)
            /*
             * A home-team change RE-PARENTS the game: `GamesController::update`
             * moves it to the division that team plays in whenever the patch
             * did not name one itself. Nothing in the patch says so, so a row
             * rebuilt from the patch alone keeps its old Division until some
             * later refetch happens to correct it.
             *
             * Only `schedule` is taken across, never the whole response —
             * `GameResource` omits the venue facilities `GamesResource`
             * carries, so swapping the row for it would strip the surface
             * options out from under the editor (see `updateGame`).
             */
            if (planned.patch.home_team_id !== undefined) {
              adoptServerSchedule(planned.gameId, saved)
            }
            return { gameId: planned.gameId, outcome: 'saved' }
          } catch (error) {
            if (isRejection(error as { status?: number })) {
              rollbackRow(planned.gameId, preEdit)
              return {
                gameId: planned.gameId,
                outcome: 'rejected',
                invalidFields: readInvalidFields(error),
                message: (error as Error)?.message,
              }
            }
            return reconcile(planned.gameId, preEdit, planned.patch)
          } finally {
            pendingByRunRef.current.get(runId)?.delete(planned.gameId)
            publishPending()
          }
        })
    )

    const results = settled
      .map((outcome) =>
        outcome.status === 'fulfilled'
          ? (outcome.value as RowResult)
          : ({
              gameId: targets[outcome.index]?.gameId ?? '',
              outcome: 'unconfirmed',
            } as RowResult)
      )
      .filter((result) => result.gameId !== '')

    const nextSummary: BulkRunSummary = {
      saved: results.filter((r) => r.outcome === 'saved').length,
      rejected: results.filter((r) => r.outcome === 'rejected').length,
      conflicted: results.filter((r) => r.outcome === 'conflict').length,
      unconfirmed: results.filter((r) => r.outcome === 'unconfirmed').length,
      notSaved: results.filter((r) => r.outcome === 'not-saved').length,
      skipped,
      results,
    }

    //  Rows that failed and rolled back are back inside the filter, so only the
    //  ones still carrying a patch stay pinned.
    const rolledBack = new Set(
      results
        .filter((r) => r.outcome === 'rejected' || r.outcome === 'not-saved')
        .map((r) => r.gameId)
    )

    //  Accumulate rather than replace. Another run may still be in flight, and
    //  its rows must not be dropped from the retention set or the summary just
    //  because this one got to the end first.
    const accumulated = accumulatedRef.current
    accumulated.results = [
      ...accumulated.results.filter(
        (existing) => !results.some((r) => r.gameId === existing.gameId)
      ),
      ...results,
    ]
    accumulated.skipped = [...accumulated.skipped, ...skipped]
    movedOutside.forEach((id) => {
      if (!rolledBack.has(id)) {
        accumulated.retained.add(id)
      }
    })
    rolledBack.forEach((id) => accumulated.retained.delete(id))

    pendingByRunRef.current.delete(runId)
    publishPending()

    markOtherVariantsStale(results.map((result) => result.gameId))
    setRetained(new Set(accumulated.retained))

    /*
     * Publish only once nothing is still running, so the admin reads ONE record
     * of what happened rather than a summary that keeps being replaced
     * underneath them — and ONLY when there is something to report.
     *
     * A run where every row saved has nothing to say. It used to say it anyway
     * ("Saved 3 games · 0 rejected · 0 not saved · 0 conflicts …"), which is a
     * strip of zeroes after every successful edit — noise that trains people to
     * dismiss the panel without reading it, which is exactly the habit it must
     * not build. Silence on success is what makes its appearance mean something.
     */
    if (pendingByRunRef.current.size === 0) {
      const settledSummary = summariseAccumulated()
      setSummary(hasSomethingToReport(settledSummary) ? settledSummary : null)
    }

    return nextSummary
  }

  /**
   * Whether a settled run is worth showing.
   *
   * A row that saved is not news, and neither is a `no-change` skip — the admin
   * opened an editor and applied the value that was already there. Everything
   * else IS: rejected and not-saved rolled back, conflict took someone else's
   * values, unconfirmed left the row in an unknown state, and a reported skip
   * means an edit was refused before it was ever sent.
   *
   * A RETAINED row is the one exception on an otherwise clean run. Dismissing
   * this panel is what releases a row the edit pushed outside the active filter;
   * with no panel there is no dismissal, and the row the admin just retimed
   * disappears from under them — the "my edit deleted the game" report that
   * retention exists to prevent. That case still gets a bar. Nothing else does.
   */
  const hasSomethingToReport = (run: BulkRunSummary): boolean =>
    run.results.some((result) => result.outcome !== 'saved') ||
    run.skipped.some((skip) => skip.reason !== 'no-change') ||
    accumulatedRef.current.retained.size > 0

  const summariseAccumulated = (): BulkRunSummary => {
    const { results, skipped } = accumulatedRef.current
    return {
      saved: results.filter((r) => r.outcome === 'saved').length,
      rejected: results.filter((r) => r.outcome === 'rejected').length,
      conflicted: results.filter((r) => r.outcome === 'conflict').length,
      unconfirmed: results.filter((r) => r.outcome === 'unconfirmed').length,
      //  `not-saved` rows are rolled back but still failed; counting them keeps
      //  the headline honest instead of only listing them below it.
      notSaved: results.filter((r) => r.outcome === 'not-saved').length,
      skipped,
      results,
    }
  }

  const dismissSummary = () => {
    setSummary(null)
    setRetained(new Set())
    accumulatedRef.current = { results: [], skipped: [], retained: new Set() }
  }

  return {
    isRunning,
    pendingIds,
    retainedOutsideFilterIds,
    isSummaryOpen: summary !== null,
    summary,
    runPatches,
    dismissSummary,
  }
}
